/**
 * SHADOW WIDGET OBSERVER - CHANGELOG
 * 
 * Natulim TS - 06-feb-26 CA-4 : Refactorización a función reutilizable + Widget Recharge
 * - PROBLEMA: Código duplicado para observar múltiples widgets con Shadow DOM
 * - SOLUCIÓN: Crear función genérica observeShadowWidget() que acepta configuración
 * - BENEFICIOS:
 *   * Una sola función para manejar todos los widgets con Shadow DOM
 *   * Configuración declarativa mediante objeto de parámetros
 *   * Callback personalizado (onFound) para lógica específica por widget
 *   * Fácil escalabilidad para agregar nuevos widgets
 * - FUNCIONES:
 *   * observeShadowWidget(): Observa y modifica cualquier widget con Shadow DOM
 * - WIDGETS CONFIGURADOS:
 *   * Aftersell (#aftersell-pdp-upsell): Estilos grid + traducciones catalán
 *   * Recharge (recharge-subscription-widget): Estilos flexbox para opciones de compra
 * 
 * Natulim TS - 12-feb-26 CA-7 : Sistema de traducciones dinámicas por ID de contenedor
 * - PROBLEMA: Traducciones hardcodeadas, mismo texto para todos los widgets Aftersell
 * - SOLUCIÓN: Sistema de mapeo de traducciones basado en el ID del div contenedor Shopify
 * - IMPLEMENTACIÓN:
 *   * Detección automática del ID del contenedor padre (shopify-block-*)
 *   * Objeto CONTAINER_TRANSLATIONS con mapeo ID -> traducciones
 *   * Fallback a traducciones DEFAULT si no existe el ID específico
 *   * Parámetro containerId añadido al callback onFound
 *   * Extracción del sufijo único del ID (parte después de último __)
 * 
 * Natulim TS - 31-mar-26 CA-XX : Traducciones Recharge Quantity Upsell Widget
 * - PROBLEMA: El widget de quantity upsell de Recharge muestra textos en inglés
 *   y no tiene sistema de traducción nativo para es/ca
 * - SOLUCIÓN: Tercer observeShadowWidget() que traversa Shadow DOMs anidados
 *   y sobreescribe textos mediante MutationObserver continuo
 * - IMPLEMENTACIÓN:
 *   * Textos en español aplicados siempre (fuente de verdad independiente de Recharge)
 *   * Si locale === 'ca', Object.assign sobreescribe con catalán
 *   * Detección del widget por presencia de recharge-quantity-upsell en su interior
 *   * Extracción dinámica de porcentajes, precios e intervalos del texto original
 *   * No modifica nunca valores monetarios (€)
 * 
 * Natulim TS - 01-abr-26 CA-X : Traducciones modal rc-learn-more-modal (en → es + ca)
 * - PROBLEMA: Modal "Learn more" de Recharge muestra 3 items genéricos en inglés
 * - SOLUCIÓN: observeShadowWidget reemplaza .learn-more-group entero con 5 items
 * - IMPLEMENTACIÓN:
 *   * Usa observeShadowWidget con targetSelector .rc-modal
 *   * onFound reemplaza innerHTML de .learn-more-group con los 5 items via template
 *   * Iconos SVG eliminados — items identificados solo por emoji en el título
 *   * LEARN_MORE_TRANSLATIONS contiene los 5 items para es y ca
 */

function observeShadowWidget(config) {
  const {
    selector,
    targetSelector,
    stylesId,
    styles,
    onFound,
    timeout = 10000
  } = config;
  
  const mainObserver = new MutationObserver(function() {
    const widget = document.querySelector(selector);
    
    if (widget && widget.shadowRoot) {
      const shadowRoot = widget.shadowRoot;
      
      const innerObserver = new MutationObserver(function() {
        const targetElement = shadowRoot.querySelector(targetSelector);
        
        if (targetElement) {
          innerObserver.disconnect();
          
          if (styles && !shadowRoot.querySelector(`#${stylesId}`)) {
            const styleElement = document.createElement('style');
            styleElement.id = stylesId;
            styleElement.textContent = styles;
            shadowRoot.appendChild(styleElement);
          }
          
          if (onFound && typeof onFound === 'function') {
            // Buscar el div padre con el ID de Shopify
            const parentDiv = widget.closest('[id^="shopify-block-"]');
            const containerId = parentDiv ? parentDiv.id : null;
            onFound(shadowRoot, targetElement, widget, containerId);
          }
        }
      });
      
      innerObserver.observe(shadowRoot, {
        childList: true,
        subtree: true
      });
      
      mainObserver.disconnect();
      setTimeout(() => innerObserver.disconnect(), timeout);
    }
  });
  
  mainObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  setTimeout(() => mainObserver.disconnect(), timeout);
}

// Natulim TS - 12-feb-26 CA-7 : Mapeo de traducciones por sufijo único del widget
// Formato: shopify-block-[DINAMICO]__aftersell_by_rokt_pdp_upsells_[SUFIJO_UNICO]
// Solo usamos el SUFIJO_UNICO como clave para evitar problemas con la parte dinámica
const CONTAINER_TRANSLATIONS = {
  'MHpn7b': { ca: { header: 'Triats junts a moltes cases', button: 'AFEGIR AL CARRETÓ' } },
  '7dMEgf': { ca: { header: 'El duet més desitjat a la rentadora', button: 'AFEGIR AL CARRETÓ' } },
  'VWbnap': { ca: { header: 'Moltes famílies els fan servir junts', button: 'AFEGIR AL CARRETÓ' } },
  'Xezm9m': { ca: { header: 'Quan la bugada s\'acaba, la casa continua...', button: 'AFEGIR AL CARRETÓ' } },
  'zqz6AX': { ca: { header: 'Per a una vaixella sense tòxics, els nostres clients ho combinen amb...', button: 'AFEGIR AL CARRETÓ' } },
  '6RXh6G': { ca: { header: 'Per a allò que el greix no perdona', button: 'AFEGIR AL CARRETÓ' } },
  'i99yhJ': { ca: { header: 'Dos bàsics que es reposen sovint', button: 'AFEGIR AL CARRETÓ' } },
  'NdFTaX': { ca: { header: 'Molt habitual junt amb els esprais', button: 'AFEGIR AL CARRETÓ' } },
  'EY4QeN': { ca: { header: 'Del greix a la llar. Un combo intel·ligent.', button: 'AFEGIR AL CARRETÓ' } },
  'amJAKb': { ca: { header: 'Per al que el greix no perdona', button: 'AFEGIR AL CARRETÓ' } },
  'QxtPXy': { ca: { header: 'Per mantenir la rutina tot el dia', button: 'AFEGIR AL CARRETÓ' } },
  'pJAQrt': { ca: { header: 'Triats junts en moltes llars', button: 'AFEGIR AL CARRETÓ' } },
  'gchXDB': { ca: { header: 'Moltes famílies ho fan servir junt', button: 'AFEGIR AL CARRETÓ' } },
  'DEFAULT': { ca: { header: 'Els nostres clients els compren junts', button: 'AFEGIR AL CARRETÓ' } }
};

function extractWidgetSuffix(containerId) {
  if (!containerId) return null;
  const parts = containerId.split('_');
  return parts[parts.length - 1];
}

// Natulim TS - 31-mar-26 CA-XX : Locale y traducciones quantity upsell
const locale = (typeof Shopify !== 'undefined' && Shopify.locale) || 'es';

const QU_T = {
  recommended:          'Recomendado',
  best_value:           'Mejor oferta',
  save_percent:         'Ahorra {{percent}}%',
  one_time:             'Compra única',
  learn_more:           'Ver más',
  purchase_options:     'Opciones de compra',
  units_delivered:      '{{qty}} unidades entregadas cada {{interval}}',
  savings_per_interval: '{{amount}} de ahorro cada {{interval}}',
  price_per_unit:       '{{price}} por unidad',
};

if (locale === 'ca') Object.assign(QU_T, {
  recommended:          'Recomanat',
  best_value:           'Millor oferta',
  save_percent:         'Estalvia {{percent}}%',
  one_time:             'Compra única',
  learn_more:           'Veure més',
  purchase_options:     'Opcions de compra',
  units_delivered:      '{{qty}} unitats lliurades cada {{interval}}',
  savings_per_interval: '{{amount}} d\'estalvi cada {{interval}}',
  price_per_unit:       '{{price}} per unitat',
});

const setText = (el, text) => {
  if (el.textContent.trim() !== text.trim()) el.textContent = text;
};

const quReplace = (str, vars) =>
  str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? _);

const translateBenefitsRoot = (benefitsRoot) => {
  benefitsRoot.querySelectorAll('li').forEach(li => {
    const txt = Array.from(li.childNodes)
      .filter(n => n.nodeType === Node.TEXT_NODE)
      .map(n => n.textContent.trim())
      .join('').trim();
    if (!txt) return;
    const units = txt.match(/^(\d+)\s+(?:unidad(?:es)?\s+entregadas?\s+cada|units?\s+delivered\s+every)\s+(.+)$/i);
    if (units) { setText(li, quReplace(QU_T.units_delivered, { qty: units[1], interval: units[2] })); return; }
    const savings = txt.match(/^([\d,.]+\s*€)\s+(?:de ahorro|savings?)\s+(?:cada|every)\s+(.+)$/i);
    if (savings) { setText(li, quReplace(QU_T.savings_per_interval, { amount: savings[1], interval: savings[2] })); return; }
    const price = txt.match(/^([\d,.]+\s*€)\s+(?:por unidad|per unit|\/ea)$/i);
    if (price) setText(li, quReplace(QU_T.price_per_unit, { price: price[1] }));
  });
};

const QU_TRANSLATORS = [
  {
    selector: '[part~="rc-quantity-upsell-badge__recommended"]',
    translate: el => setText(el, QU_T.recommended)
  },
  {
    selector: '[part~="rc-quantity-upsell-badge__best-value"]',
    translate: el => setText(el, QU_T.best_value)
  },
  {
    selector: '[part~="rc-quantity-upsell-badge__savings"]',
    translate: el => {
      const pct = el.textContent.match(/\d+/)?.[0];
      if (pct) setText(el, quReplace(QU_T.save_percent, { percent: pct }));
    }
  },
  {
    selector: '[rc-onetime] .rc-quantity-upsell-option__header > div:first-child',
    translate: el => { el.textContent = QU_T.one_time; }
  },
  {
    selector: '[part~="rc-learn-more__trigger"]',
    translate: el => setText(el, QU_T.learn_more)
  },
  {
    selector: 'legend.visually-hidden',
    translate: el => setText(el, QU_T.purchase_options)
  },
  {
    // rc-benefits tiene su propio shadow root — se maneja por separado
    selector: 'rc-benefits',
    translate: el => {
      if (el.shadowRoot) translateBenefitsRoot(el.shadowRoot);
    }
  }
];

const quTranslateRoot = root => {
  for (const { selector, translate } of QU_TRANSLATORS) {
    root.querySelectorAll(selector).forEach(translate);
  }
  root.querySelectorAll('*').forEach(el => {
    if (el.shadowRoot) quTranslateRoot(el.shadowRoot);
  });
};

// Natulim TS - 01-abr-26 CA-X : Traducciones modal rc-learn-more-modal (en → es + ca)
// 5 items por locale — reemplaza el .learn-more-group entero via innerHTML
const LEARN_MORE_TRANSLATIONS = {
  es: {
    header: '¿Por qué suscribirte?',
    button: 'Entendido, quiero suscribirme',
    items: [
      {
        title: '💰 Ahorra hasta un 30% siempre',
        description: 'No es una oferta temporal. Es el precio real que pagas cada vez. Más barato que cualquier compra única.'
      },
      {
        title: '🔔 Nunca más te quedas sin producto',
        description: 'Llega automáticamente cuando lo necesitas. Sin pensar. Sin recordatorios. Sin emergencias de última hora.'
      },
      {
        title: '✏️ Cancela cuando quieras, sin trampa',
        description: 'Un clic y listo. Sin llamadas, sin preguntas incómodas. Control total desde tu portal de cliente.'
      },
      {
        title: '🎁 Regalos exclusivos automáticos',
        description: 'Limpia lavadoras, cepillos de bambú, bolsas de lavado... Te van llegando según tu nivel de suscripción.'
      },
      {
        title: '🌍 Envíos optimizados, menos CO₂',
        description: 'Agrupamos entregas, evitamos plástico y reducimos emisiones. Cada suscripción cuida el planeta.'
      }
    ]
  },
  ca: {
    header: 'Per què subscriure\'s?',
    button: 'Entès, vull subscriure\'m',
    items: [
      {
        title: '💰 Estalvia fins a un 30% sempre',
        description: 'No és una oferta temporal. És el preu real que pagues cada vegada. Més barat que qualsevol compra única.'
      },
      {
        title: '🔔 Mai més et quedis sense producte',
        description: 'Arriba automàticament quan ho necessites. Sense pensar. Sense recordatoris. Sense emergències d\'última hora.'
      },
      {
        title: '✏️ Cancel·la quan vulguis, sense trampa',
        description: 'Un clic i llest. Sense trucades, sense preguntes incòmodes. Control total des del teu portal de client.'
      },
      {
        title: '🎁 Regals exclusius automàtics',
        description: 'Neteja rentadores, raspalls de bambú, bosses de rentat... Et van arribant segons el teu nivell de subscripció.'
      },
      {
        title: '🌍 Enviaments optimitzats, menys CO₂',
        description: 'Agrupem lliuraments, evitem plàstic i reduïm emissions. Cada subscripció cuida el planeta.'
      }
    ]
  }
};

document.addEventListener('DOMContentLoaded', function() {

  // ─── Widget 1: Aftersell PDP Upsell ───────────────────────────────────────
  observeShadowWidget({
    selector: '#aftersell-pdp-upsell',
    targetSelector: '.pdp-widget',
    stylesId: 'custom-styles',
    styles: `
      .pdp-widget .vertical-layout-divider {
        display: grid !important;
        grid-template-columns: 1fr .75fr !important; 
        gap: 14px !important;
        font-family: Bricolage Grotesque !important;
      }
      
      .pdp-widget .product-title,
      .pdp-widget .select-dropdown {
        font-weight: 500 !important;
      }
      
      .pdp-widget .select-dropdown {
        padding: 0px 16px !important;
        text-align: start !important;
        height: 40px !important;
      }
      .upsell-header {
        font-family: Bricolage Grotesque !important;
      }
      
      @media (max-width: 768px) {
        .pdp-widget .vertical-layout-divider {
          grid-template-columns: 1fr !important;
        }
        .pdp-widget .product-card { padding: 8px !important; }
        .pdp-widget .product-image { width: 120px !important; height: 120px !important; }
        .pdp-widget .product-image-wrapper { align-items: start; }
        .pdp-widget .product-info { padding-bottom: 0px !important; }
        .pdp-widget .select-dropdown { height: 35px !important; }
        .pdp-widget .add-button { height: 40px !important; }
        .pdp-widget .carousel-button { display: none !important; }
        .pdp-widget .product-card-container.horizontal { flex-direction: column; width: 100%; max-width: 100%; align-items: center; }
        .pdp-widget .carousel-wrapper { justify-content: start; }
        .pdp-widget .product-card.horizontal { flex-direction: row; max-width: 90%; }
      }
    `,
    onFound: function(shadowRoot, targetElement, widget, containerId) {
      if (typeof Shopify !== 'undefined' && Shopify.locale === 'ca') {
        // Natulim TS - 12-feb-26 CA-7 : Extraer sufijo único y obtener traducciones
        const widgetSuffix = extractWidgetSuffix(containerId);
        const translations = CONTAINER_TRANSLATIONS[widgetSuffix] || CONTAINER_TRANSLATIONS['DEFAULT'];
        
        // Aplicar traducciones
        const upsellHeader = shadowRoot.querySelector('.pdp-widget .upsell-header');
        if (upsellHeader && translations.ca.header) upsellHeader.textContent = translations.ca.header;
        const addButton = shadowRoot.querySelector('.pdp-widget .add-button');
        if (addButton && translations.ca.button) addButton.textContent = translations.ca.button;
      }
    }
  });

  // ─── Widget 2: Recharge Subscription Widget (estilos compra única) ────────
  observeShadowWidget({
    selector: 'recharge-subscription-widget',
    targetSelector: '.rc-purchase-option.rc-selection__onetime',
    stylesId: 'custom-onetime-styles',
    styles: `
      .rc-purchase-option.rc-selection__onetime 
      .rc-purchase-option__label {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 8px !important;
      }
      .rc-purchase-option.rc-selection__onetime 
      .rc-purchase-option__prices {
        margin-left: 0 !important;
      }
    `
  });

  // ─── Widget 3: Recharge Quantity Upsell (traducciones es/ca) ──────────────
  observeShadowWidget({
    selector: 'recharge-subscription-widget',
    targetSelector: 'recharge-quantity-upsell',
    onFound: function(shadowRoot) {
      const quWidget = shadowRoot.querySelector('recharge-quantity-upsell');
      if (!quWidget) return;

      const translateBenefits = (el) => {
        if (!el.shadowRoot) return;
        const benefitsRoot = el.shadowRoot;
        translateBenefitsRoot(benefitsRoot);
        if (!el._rcObserved) {
          el._rcObserved = true;
          new MutationObserver(() => translateBenefitsRoot(benefitsRoot))
            .observe(benefitsRoot, { childList: true, subtree: true });
        }
      };

      const applyWhenReady = (quRoot) => {
        // Pequeño delay para asegurar que el one-time option está renderizado
        setTimeout(() => quTranslateRoot(quRoot), 100);
        quRoot.querySelectorAll('rc-benefits').forEach(translateBenefits);

        new MutationObserver((mutations) => {
          mutations.forEach(m => {
            m.addedNodes.forEach(node => {
              if (node.nodeType !== Node.ELEMENT_NODE) return;
              setTimeout(() => {
                const benefits = node.nodeName === 'RC-BENEFITS'
                  ? [node]
                  : [...node.querySelectorAll('rc-benefits')];
                benefits.forEach(translateBenefits);
              }, 0);
            });
          });
        }).observe(quRoot, { childList: true, subtree: true });
      };

      if (quWidget.shadowRoot) {
        applyWhenReady(quWidget.shadowRoot);
      } else {
        new MutationObserver((_, obs) => {
          if (quWidget.shadowRoot) { obs.disconnect(); applyWhenReady(quWidget.shadowRoot); }
        }).observe(quWidget, { childList: true, subtree: true });
      }
    }
  });

  // ─── Widget 4: Recharge Learn More Modal (traducciones en → es/ca) ────────
  // Natulim TS - 01-abr-26 CA-X
  if (typeof Shopify !== 'undefined' && LEARN_MORE_TRANSLATIONS[Shopify.locale]) {
    observeShadowWidget({
      selector: 'rc-learn-more-modal',
      targetSelector: '.rc-modal',
      onFound: function() {
        const root = document.querySelector('rc-learn-more-modal');
        if (!root) return;

        const t = LEARN_MORE_TRANSLATIONS[Shopify.locale];

        // Reemplazar el grupo entero con los 5 items
        const group = root.querySelector('.learn-more-group');
        if (group) {
          group.innerHTML = t.items.map(item => `
            <li class="learn-more-group-item">
              <div>
                <h2>${item.title}</h2>
                <p>${item.description}</p>
              </div>
            </li>
          `).join('');
        }

        // Header y botón
        const header = root.querySelector('.learn-more-header');
        if (header) header.textContent = t.header;

        const button = root.querySelector('[data-dismiss-modal]');
        if (button) button.textContent = t.button;
      }
    });
  }

});